import React from 'react';
import { Users } from 'lucide-react';
import ActivityFeedItem from '../CallAgent/ActivityFeedItem';
import type { TaskFeedItem } from '../../hooks/useTasksQueue';
import { formatInTz } from '../../utils/dateFormatters';
import { useTimezone } from '../../hooks/useTimezone';

interface LeadGenActivityFeedProps {
  processingTasks: TaskFeedItem[];
  recentFinishedTasks: TaskFeedItem[];
  hasProcessing: boolean;
  hasAnyTasks: boolean;
}

const LeadGenActivityFeed: React.FC<LeadGenActivityFeedProps> = ({
  processingTasks,
  recentFinishedTasks,
  hasProcessing,
  hasAnyTasks,
}) => {
  const tz = useTimezone();

  if (!hasAnyTasks) {
    return (
      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-12 text-center shadow-xl flex flex-col items-center justify-center" style={{ height: '600px', minHeight: '600px', maxHeight: '600px' }}>
        <div className="w-20 h-20 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Users className="text-[#1875FF]" size={36} />
        </div>
        <h3 className="text-lg font-extrabold text-gray-900 mb-2 font-display">Waiting for lead requests</h3>
        <p className="text-gray-700 text-sm font-medium">Place a new lead request to get me started</p>
      </div>
    );
  }

  const remainingSlots = 6 - processingTasks.length;
  const fillerTasks = recentFinishedTasks.slice(0, Math.max(0, remainingSlots));
  const displayActivities = [...processingTasks, ...fillerTasks];

  const lastUpdated = recentFinishedTasks[0]?.timestamp;
  const subtitleText = hasProcessing
    ? 'Working now...'
    : lastUpdated
      ? `Last activity at ${formatInTz(lastUpdated, 'HH:mm', tz)}`
      : 'Waiting for activity...';

  return (
    <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-xl flex flex-col" style={{ height: '600px', minHeight: '600px', maxHeight: '600px' }}>
      <div className="p-6 border-b border-[#1875FF]/10 bg-gradient-to-r from-[#05E0D1]/10 to-[#1875FF]/10 flex-shrink-0">
        <h3 className="text-lg font-extrabold text-gray-900 flex items-center space-x-2 font-display">
          <div className={`w-2 h-2 rounded-full ${hasProcessing ? 'bg-[#05E0D1] animate-pulse' : 'bg-gray-400'}`}></div>
          <span>Live Activity Feed</span>
        </h3>
        <p className="text-sm text-gray-700 mt-1 font-medium">
          {subtitleText}
        </p>
      </div>
      <div className="flex-1 p-6 overflow-hidden">
        <div className="space-y-2.5">
          {displayActivities.map((activity) => {
            const isProcessing = activity.status === 'processing';
            return (
              <div
                key={activity.taskId}
                className={isProcessing ? 'animate-in slide-in-from-top duration-500' : ''}
              >
                <ActivityFeedItem
                  activity={activity}
                  icon={activity.icon}
                  iconColor=""
                  description={activity.description}
                  isHighlighted={isProcessing}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LeadGenActivityFeed;
